import {
  Drawer,
  Text,
  DrawerBody,
  VStack,
  HStack,
  Button,
  Icon,
  Box,
  Avatar,
  Accordion,
  DrawerFooter,
  CloseButton,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import MobileLogoutButton from "./MobileLogoutButton";
import { useUserContext } from "../../Contexts/userContext";
import useIcons from "../../hooks/useIcons";
import useThemeColors from "../../hooks/useThemeColors";
import { ColorModeButton } from "../ui/color-mode";
import useAuth from "../../hooks/useAuth";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileMenu({ isOpen, onClose }: Props) {
  const { user } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const icons = useIcons();
  const colors = useThemeColors();
  const isAdmin = user?.role === 3;
  const { logout } = useAuth();

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleProfileNavigation = () => {
    navigate("/profile");
    onClose();
  };

  const isActiveLink = (path: string): boolean => {
    return location.pathname === path;
  };

  // Core navigation items
  const coreNavItems = [
    { name: "Dashboard", path: "/", icon: icons.main.home },
    { name: "Students", path: "/students", icon: icons.people.students },
    { name: "Results", path: "/results", icon: icons.main.result },
  ];

  // Admin navigation groups
  const adminNavGroups = [
    {
      name: "School Management",
      items: [
        { name: "School Settings", path: "/admin/school", icon: icons.main.school },
      ],
    },
    {
      name: "Academic Management",
      items: [
        {
          name: "Sessions",
          path: "/admin/academic/sessions",
          icon: icons.main.session,
        },
        { name: "Terms", path: "/admin/academic/terms", icon: icons.main.term },
        { name: "Levels", path: "/admin/academic/levels", icon: icons.main.level },
        {
          name: "Classes",
          path: "/admin/academic/classes",
          icon: icons.main.class,
        },
        {
          name: "Subjects",
          path: "/admin/academic/subjects",
          icon: icons.main.subject,
        },
        {
          name: "Assessment Types",
          path: "/admin/academic/assessment-types",
          icon: icons.main.assessment,
        },
        { name: "Grades", path: "/admin/academic/grades", icon: icons.main.grade },
      ],
    },
    {
      name: "User Management",
      items: [
        { name: "Users", path: "/admin/users", icon: icons.people.user },
        { name: "Audit Trail", path: "/admin/audit-trail", icon: icons.main.audit },
        {
          name: "Notifications",
          path: "/admin/notifications",
          icon: icons.main.notifications,
        },
      ],
    },
    {
      name: "Data Operations",
      items: [
        {
          name: "Export Data",
          path: "/admin/data/export",
          icon: icons.utility.upload,
        },
        {
          name: "Delete Data",
          path: "/admin/data/delete",
          icon: icons.utility.delete,
        },
        {
          name: "Bulk Reports",
          path: "/admin/data/bulk-reports",
          icon: icons.utility.print,
        },
      ],
    },
    {
      name: "Promotion System",
      items: [
        {
          name: "Promotions",
          path: "/admin/promotion",
          icon: icons.main.promotion,
        },
      ],
    },
  ];

  return (
    <Drawer.Root placement="end" open={isOpen} size="sm">
      <Drawer.Backdrop />
      <Drawer.Positioner>
        <Drawer.Content
          bg={colors.background.primary}
          borderLeft="1px"
          borderColor={colors.border.primary}
        >
          <Drawer.CloseTrigger asChild onClick={onClose}>
            <CloseButton size="sm" />
          </Drawer.CloseTrigger>
          <Drawer.Header></Drawer.Header>
          {/* Header - Fixed */}
          <Box
            p={4}
            pt={12}
            borderBottom="1px"
            borderColor={colors.border.primary}
          >
            <HStack gap={3} align="center">
              <Avatar.Root size="md">
                <Avatar.Image src={user?.avatarUrl || ""} />
                <Avatar.Fallback
                  name={user?.firstName + " " + user?.lastName}
                ></Avatar.Fallback>
              </Avatar.Root>
              <VStack align="start" gap={0} flex={1}>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={colors.text.primary}
                >
                  {user?.firstName} {user?.lastName}
                </Text>
                <Text fontSize="xs" color={colors.text.secondary}>
                  {user?.email}
                </Text>
              </VStack>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleProfileNavigation}
                borderRadius="lg"
                _hover={{ bg: colors.background.secondary }}
              >
                <Icon as={icons.people.user} boxSize={4} />
              </Button>
            </HStack>
          </Box>
          <DrawerBody p={0}>
            {/* Core Navigation - Fixed */}
            <Box p={4} borderBottom="1px" borderColor={colors.border.primary}>
              <VStack gap={1} align="stretch">
                {coreNavItems.map((item) => (
                  <Button
                    key={item.path}
                    variant="ghost"
                    justifyContent="flex-start"
                    onClick={() => handleNavigation(item.path)}
                    bg={
                      isActiveLink(item.path)
                        ? colors.brand.navPrimaryButton
                        : "transparent"
                    }
                    color={
                      isActiveLink(item.path) ? "white" : colors.text.primary
                    }
                    _hover={{
                      bg: isActiveLink(item.path)
                        ? colors.brand.navPrimaryButton
                        : colors.background.secondary,
                    }}
                    borderRadius="lg"
                    h="48px"
                    fontSize="sm"
                    fontWeight="medium"
                  >
                    <Icon as={item.icon} boxSize={5} mr={2} />
                    {item.name}
                  </Button>
                ))}
              </VStack>
            </Box>

            {/* Admin Navigation - Scrollable */}
            {isAdmin && (
              <Box
                flex={1}
                overflowY="auto"
                borderBottom="1px"
                borderColor={colors.border.primary}
              >
                <Box p={4}>
                  <HStack justify="space-between" align="center" mb={3}>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color={colors.text.secondary}
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      Admin
                    </Text>
                  </HStack>

                  <Accordion.Root multiple>
                    {adminNavGroups.map((group, index) => (
                      <Accordion.Item
                        value={group.name}
                        key={index}
                        border="none"
                        mb={2}
                      >
                        <Accordion.ItemTrigger
                          bg={colors.background.secondary}
                          borderRadius="lg"
                          _hover={{ bg: colors.background.tertiary }}
                          px={3}
                          py={2}
                        >
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color={colors.text.primary}
                            flex="1"
                            textAlign="left"
                          >
                            {group.name}
                          </Text>
                          <Accordion.ItemIndicator
                            color={colors.text.secondary}
                          />
                        </Accordion.ItemTrigger>
                        <Accordion.ItemContent px={0} pt={2}>
                          <VStack gap={1} align="stretch">
                            {group.items.map((item) => (
                              <Button
                                key={item.path}
                                variant="ghost"
                                justifyContent="flex-start"
                                onClick={() => handleNavigation(item.path)}
                                bg={
                                  isActiveLink(item.path)
                                    ? colors.brand.navPrimaryButton
                                    : "transparent"
                                }
                                color={
                                  isActiveLink(item.path)
                                    ? "white"
                                    : colors.text.primary
                                }
                                _hover={{
                                  bg: isActiveLink(item.path)
                                    ? colors.brand.navPrimaryButton
                                    : colors.background.secondary,
                                }}
                                borderRadius="lg"
                                h="40px"
                                fontSize="sm"
                                fontWeight="normal"
                                opacity={isActiveLink(item.path) ? 1 : 0.8}
                                pl={6}
                              >
                                <Icon as={item.icon} boxSize={4} mr={2} />
                                {item.name}
                              </Button>
                            ))}
                          </VStack>
                        </Accordion.ItemContent>
                      </Accordion.Item>
                    ))}
                  </Accordion.Root>
                </Box>
              </Box>
            )}
          </DrawerBody>
          <DrawerFooter bg={colors.background.tertiary}>
            {/* Footer - Fixed */}
            <HStack w="full" gap={3} align="stretch" justify="space-between">
              <MobileLogoutButton handleLogout={logout} />
              <ColorModeButton />
            </HStack>
          </DrawerFooter>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
